import { useState } from 'react';
import { Download, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';
import { PipelineNode, PipelineEdge } from '../types/pipeline';

interface PipelineExportProps {
  pipelineIds: string[];
  pipelineNames: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ExportedPipeline {
  id: string;
  name: string;
  description?: string;
  nodes: PipelineNode[];
  edges: PipelineEdge[];
  exportedAt: string;
  version: string;
}

export function PipelineExportDialog({
  pipelineIds,
  pipelineNames,
  open,
  onOpenChange,
}: PipelineExportProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [exportedData, setExportedData] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const pipelines = await Promise.all(
        pipelineIds.map((id) => apiClient.get(`/pipelines/${id}`))
      );

      const exportData: ExportedPipeline[] = pipelines.map((p: any) => ({
        id: p.id,
        name: p.name,
        description: p.description,
        nodes: p.nodes || [],
        edges: p.edges || [],
        exportedAt: new Date().toISOString(),
        version: '1.0.0',
      }));

      const json = JSON.stringify(exportData, null, 2);
      setExportedData(json);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to export pipelines');
    } finally {
      setIsExporting(false);
    }
  };

  const handleCopyToClipboard = async () => {
    if (exportedData) {
      try {
        await navigator.clipboard.writeText(exportedData);
        setCopied(true);
        toast.success('Copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
      } catch {
        toast.error('Failed to copy to clipboard');
      }
    }
  };

  const handleDownload = () => {
    if (exportedData) {
      const blob = new Blob([exportedData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pipelines-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Pipeline definitions downloaded');
    }
  };

  const handleClose = () => {
    setExportedData(null);
    setCopied(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Export Pipeline Definitions</DialogTitle>
          <DialogDescription>
            Export {pipelineIds.length} pipeline{pipelineIds.length > 1 ? 's' : ''} as JSON for backup or migration.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Pipeline Names */}
          <div className="space-y-2">
            <Label>Pipelines to Export</Label>
            <div className="flex flex-wrap gap-2">
              {pipelineNames.map((name) => (
                <span key={name} className="px-2 py-1 bg-muted rounded text-sm">
                  {name}
                </span>
              ))}
            </div>
          </div>

          {/* Exported Data */}
          {!exportedData && !isExporting && (
            <div className="text-center py-8 border-2 border-dashed rounded-lg">
              <Download className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-4">
                Click the button below to generate the export file
              </p>
              <Button onClick={handleExport} className="mx-auto">
                <Download className="h-4 w-4 mr-2" />
                Generate Export
              </Button>
            </div>
          )}

          {isExporting && (
            <div className="text-center py-8">
              <div className="animate-pulse text-muted-foreground">
                Generating export...
              </div>
            </div>
          )}

          {exportedData && (
            <div className="space-y-2">
              <Label>Exported JSON</Label>
              <Textarea
                readOnly
                value={exportedData}
                className="h-48 font-mono text-xs"
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
          {exportedData && (
            <>
              <Button variant="outline" onClick={handleCopyToClipboard}>
                {copied ? 'Copied!' : 'Copy to Clipboard'}
              </Button>
              <Button onClick={handleDownload}>
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface PipelineImportProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function PipelineImportDialog({
  open,
  onOpenChange,
  onSuccess,
}: PipelineImportProps) {
  const [jsonData, setJsonData] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [parsedData, setParsedData] = useState<ExportedPipeline[] | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const handleParse = () => {
    setParseError(null);
    try {
      const parsed = JSON.parse(jsonData);
      if (!Array.isArray(parsed)) {
        throw new Error('Import data must be an array of pipelines');
      }
      setParsedData(parsed);
    } catch (err: any) {
      setParseError(err.message || 'Invalid JSON format');
      setParsedData(null);
    }
  };

  const handleImport = async () => {
    if (!parsedData || parsedData.length === 0) {
      toast.error('No pipelines to import');
      return;
    }

    setIsImporting(true);
    try {
      for (const pipeline of parsedData) {
        await apiClient.post('/pipelines', {
          name: `${pipeline.name} (Imported)`,
          description: pipeline.description,
          nodes: pipeline.nodes || [],
          edges: pipeline.edges || [],
        });
      }
      toast.success(`Successfully imported ${parsedData.length} pipeline${parsedData.length > 1 ? 's' : ''}`);
      setJsonData('');
      setParsedData(null);
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to import pipelines');
    } finally {
      setIsImporting(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result as string;
        setJsonData(content);
        handleParse();
      };
      reader.readAsText(file);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onOpenChange(false)}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Import Pipeline Definitions</DialogTitle>
          <DialogDescription>
            Import pipeline definitions from a JSON file or paste the JSON directly.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* File Upload */}
          <div className="space-y-2">
            <Label>Upload JSON File</Label>
            <div className="flex items-center gap-2">
              <input
                type="file"
                accept=".json"
                onChange={handleFileUpload}
                className="flex-1 text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/80"
              />
            </div>
          </div>

          <div className="text-center text-sm text-muted-foreground">
            or paste JSON directly below
          </div>

          {/* JSON Input */}
          <div className="space-y-2">
            <Label>Pipeline JSON</Label>
            <Textarea
              value={jsonData}
              onChange={(e) => setJsonData(e.target.value)}
              placeholder='[{"id": "...", "name": "...", "nodes": [...], "edges": [...]}]'
              className="h-48 font-mono text-xs"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleParse}
              className="w-full"
            >
              Parse JSON
            </Button>
          </div>

          {/* Parse Error */}
          {parseError && (
            <div className="p-3 bg-destructive/10 border border-destructive rounded-md">
              <p className="text-sm text-destructive">{parseError}</p>
            </div>
          )}

          {/* Parsed Data Preview */}
          {parsedData && (
            <div className="space-y-2">
              <Label>Pipelines to Import</Label>
              <div className="border rounded-md p-3 space-y-2">
                {parsedData.map((pipeline, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <span className="font-medium">{pipeline.name}</span>
                    <span className="text-muted-foreground">{pipeline.nodes.length} nodes</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setJsonData('');
              setParsedData(null);
              setParseError(null);
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={!parsedData || isImporting}
          >
            {isImporting ? (
              'Importing...'
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Import {parsedData?.length || 0} Pipeline{parsedData && parsedData.length > 1 ? 's' : ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
